import { MetaModelViewGraphNode } from '@ardoq/graph';
import {
  EditablePathCollapsingRule,
  ViewStateEditablePathCollapsingRule,
  ViewStatePathCollapsingRule,
} from './types';
import { DirectedTriple } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import {
  getNodeMetaDataFromGraphNodeId,
  getNodeMetaDataFromTypeName,
} from '../getNodeMetaData';
import { PathCollapsingRuleInternal } from 'viewpointBuilder/types';

const getStartNodeFromPath = (
  path: DirectedTriple[],
  graphNodes: Map<string, MetaModelViewGraphNode>,
  isHierarchyWildcardActive: boolean
) => {
  if (!path.length) {
    return null;
  }
  const firstTriple = path[0];
  const startTypeFromPath =
    firstTriple.direction === 'outgoing'
      ? firstTriple.sourceType
      : firstTriple.targetType;
  return getNodeMetaDataFromTypeName(
    graphNodes,
    startTypeFromPath,
    isHierarchyWildcardActive
  );
};

const getEndNodeFromPath = (
  path: DirectedTriple[],
  graphNodes: Map<string, MetaModelViewGraphNode>,
  isHierarchyWildcardActive: boolean
) => {
  if (!path.length) {
    return null;
  }
  const lastTriple = path[path.length - 1];
  const endTypeFromPath =
    lastTriple.direction === 'outgoing'
      ? lastTriple.targetType
      : lastTriple.sourceType;

  return getNodeMetaDataFromTypeName(
    graphNodes,
    endTypeFromPath,
    isHierarchyWildcardActive
  );
};

export const transformCurrentEditableRule = (
  currentEditableRule: EditablePathCollapsingRule,
  graphNodes: Map<string, MetaModelViewGraphNode>,
  isHierarchyWildcardActive: boolean
): ViewStateEditablePathCollapsingRule => {
  const { path, startNodeId, displayText, errorMessage } = currentEditableRule;
  const startNode =
    getStartNodeFromPath(path, graphNodes, isHierarchyWildcardActive) ??
    getNodeMetaDataFromGraphNodeId(
      graphNodes,
      startNodeId,
      isHierarchyWildcardActive
    );
  const endNode = getEndNodeFromPath(
    path,
    graphNodes,
    isHierarchyWildcardActive
  );
  return {
    ...currentEditableRule,
    startNode,
    endNode,
    isSavable: Boolean(path.length > 0 && displayText && !errorMessage),
    // Only a recursive rule absorbs the hierarchy, so only it can keep levels of
    // it expanded.
    keepExpandedLevels:
      currentEditableRule.collapseRecursively?.keepExpandedLevels ?? null,
  };
};

const getCollapsedPath = (
  path: DirectedTriple[],
  graphNodes: Map<string, MetaModelViewGraphNode>,
  isHierarchyWildcardActive: boolean
) => {
  if (!graphNodes.size) return [];
  return path
    .map(triple => {
      const componentTypeMetaData = getNodeMetaDataFromTypeName(
        graphNodes,
        triple.direction === 'outgoing' ? triple.targetType : triple.sourceType,
        isHierarchyWildcardActive
      );
      if (!componentTypeMetaData) {
        logError(Error('Could not find component type metadata for triple'));
        return null;
      }
      return {
        direction: triple.direction,
        referenceType:
          triple.referenceType === 'ardoq_parent'
            ? 'Is parent of'
            : triple.referenceType,
        componentTypeMetaData,
      };
    })
    .filter(isTruthy);
};

export const transformRules = (
  rules: PathCollapsingRuleInternal[],
  graphNodes: Map<string, MetaModelViewGraphNode>,
  isHierarchyWildcardActive: boolean
): ViewStatePathCollapsingRule[] =>
  rules.map(rule => {
    const { path, displayText, isExpanded, isActive, referenceTypeId } = rule;
    return {
      referenceTypeId,
      displayText,
      startNode: path
        ? getStartNodeFromPath(path, graphNodes, isHierarchyWildcardActive)
        : null,
      endNode: path
        ? getEndNodeFromPath(path, graphNodes, isHierarchyWildcardActive)
        : null,
      collapsedPath: path
        ? getCollapsedPath(path, graphNodes, isHierarchyWildcardActive)
        : null,
      isExpanded,
      isActive,
    };
  });
